import { useEffect, useRef, useState } from 'react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_KEYS,
  changeDue,
  balanceLabel,
  exceedsCreditLimit,
  formatAdjust,
  paymentForAmount,
  paymentForBase,
  paymentTotals,
  type PaymentLine,
  type PaymentMethod,
  type PosSettings,
} from '@pos/core';
import { Alert, Button, cx } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { cashSuggestions, shortCash } from './tender.ts';

/** Flechas en la grilla de medios de pago (dos columnas). */
const ARROW_STEPS: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -2, ArrowDown: 2 };

/** Pantalla táctil: no se enfoca «paga con» (abriría el teclado del celular encima de los billetes). */
const touchScreen = () => matchMedia('(pointer: coarse)').matches;

/** Cliente elegido para la venta (para cobrar a cuenta corriente). */
export interface CheckoutCustomer {
  name: string;
  balance: number;
  creditLimit: number | null;
  /** El dueño puede pasar el límite de crédito (con aviso); un empleado no. */
  canExceedLimit: boolean;
}

export function CheckoutDialog({
  open,
  baseTotal,
  settings,
  customer,
  canUseAccount,
  money,
  busy,
  error,
  onClose,
  onConfirm,
  onPickCustomer,
}: {
  open: boolean;
  baseTotal: number;
  settings: PosSettings;
  customer: CheckoutCustomer | null;
  /** Puede vender a cuenta corriente (permiso), haya o no cliente elegido. */
  canUseAccount: boolean;
  money: (c: number) => string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (payments: PaymentLine[]) => void;
  /** «Cuenta corriente» sin cliente: abre «Elegir cliente» y, si se elige uno, llama a onPicked. */
  onPickCustomer: (onPicked: () => void) => void;
}) {
  const [payments, setPayments] = useState<PaymentLine[]>([]);
  const [split, setSplit] = useState(false);
  const [partial, setPartial] = useState<number | null>(null);
  const methodsRef = useRef<HTMLDivElement>(null);
  const paymentsRef = useRef<HTMLUListElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Al abrir (o si cambia el total) arranca de cero en el mismo render, no en un efecto después: si
  // no, por un instante quedaba el pago de la venta anterior y un Enter rápido cobraba con ese pago.
  const [startedFor, setStartedFor] = useState<number | null>(null);
  const start = open ? baseTotal : null;
  if (start !== startedFor) {
    setStartedFor(start);
    if (open) {
      setPayments([]);
      setSplit(false);
      setPartial(null);
    }
  }

  // «Cuenta corriente» aparece aunque todavía no se haya elegido el cliente: al tocarla se elige (ver choose).
  const accountEnabled = settings.payments.account.enabled;
  const accountAllowed = canUseAccount && accountEnabled;
  const methods = PAYMENT_METHOD_KEYS.filter((m) => settings.payments[m].enabled && (m !== 'account' || accountAllowed));
  const onAccount = payments.filter((p) => p.method === 'account').reduce((a, p) => a + p.amount, 0);
  const overLimit = !!customer && onAccount > 0 && exceedsCreditLimit(customer.balance, onAccount, customer.creditLimit);
  const blockedByLimit = overLimit && !customer!.canExceedLimit;
  // El cliente recién elegido tarda un instante en cargarse: hasta entonces no se puede revisar su
  // límite, así que no se confirma.
  const waitingCustomer = onAccount > 0 && !customer;
  const paid = paymentTotals(payments);
  const remaining = baseTotal - paid.base;
  const cashOk = payments.every((p) => p.method !== 'cash' || p.tendered == null || p.tendered >= p.amount);
  const ready = remaining === 0 && payments.length > 0 && cashOk && !blockedByLimit && !waitingCustomer;
  const change = changeDue(payments);
  const pending = remaining > 0;
  const lastMethod = payments[payments.length - 1]?.method;
  const methodButtons = () => [...(methodsRef.current?.querySelectorAll('button') ?? [])];

  // Teclado: al abrir y después de cada pago, el foco va a lo que sigue: el primer medio de pago
  // mientras falte cubrir; si no, «paga con» (efectivo) o «Confirmar venta» (también cuando termina
  // de cargarse el cliente elegido para la cuenta corriente: antes el botón estaba deshabilitado).
  useEffect(() => {
    if (!open) return;
    if (pending) {
      methodButtons()[0]?.focus();
    } else if (lastMethod === 'cash' && !touchScreen()) {
      [...(paymentsRef.current?.querySelectorAll<HTMLInputElement>('input[data-tendered]') ?? [])].at(-1)?.focus();
    } else {
      confirmRef.current?.focus();
    }
  }, [open, payments.length, pending, lastMethod, waitingCustomer]);

  const add = (method: PaymentMethod) => {
    if (remaining <= 0) return;
    const line = split && partial ? paymentForAmount(method, partial, remaining, settings) : paymentForBase(method, remaining, settings);
    if (line.base <= 0) return;
    setPayments((list) => [...list, line]);
    setPartial(null);
  };

  // Cuenta corriente sin cliente: primero se elige (o se crea) y después se anota lo que falta. Si
  // se cancela, no se anota nada.
  const choose = (method: PaymentMethod) => (method === 'account' && !customer ? onPickCustomer(() => add('account')) : add(method));

  // «Paga con» menos de lo que tenía que pagar en efectivo: el efectivo queda en lo que dio y lo que
  // falta se anota en la cuenta corriente (eligiendo el cliente si hace falta) o queda para otro medio.
  const payRest = (index: number, rest: 'account' | 'other') => {
    const apply = () =>
      setPayments((list) => {
        const short = list[index] && shortCash(list[index], settings);
        if (!short) return list;
        const next = list.map((p, i) => (i === index ? short.cash : p));
        return rest === 'account' ? [...next, paymentForBase('account', short.restBase, settings)] : next;
      });
    if (rest === 'account' && !customer) onPickCustomer(apply);
    else apply();
  };
  const anyShort = payments.some((p) => shortCash(p, settings));

  const setTendered = (index: number, tendered: number | null) =>
    setPayments((list) => list.map((p, i) => (i === index ? { ...p, tendered } : p)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Cobrar"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button ref={confirmRef} size="lg" disabled={!ready} loading={busy} onClick={() => onConfirm(payments)}>
            Confirmar venta
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) onConfirm(payments);
          // Enter en «monto de este pago» (dividir el pago): sigue en los medios de pago.
          else if (pending) methodButtons()[0]?.focus();
          // Enter en «paga con» con menos de lo que había que pagar: a qué hacer con lo que falta.
          else paymentsRef.current?.querySelector<HTMLButtonElement>('button[data-rest]')?.focus();
        }}
      >
        <div className="flex items-baseline justify-between">
          <span className="text-slate-600">Total</span>
          <span className="text-2xl font-semibold tabular-nums">{money(baseTotal + paid.adjustment + paid.rounding)}</span>
        </div>

        {payments.length > 0 && (
          <ul ref={paymentsRef} className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {payments.map((p, i) => {
              const adjust = formatAdjust(settings.payments[p.method].adjust_bps);
              const suggestions = p.method === 'cash' ? cashSuggestions(p.amount) : [];
              const short = shortCash(p, settings);
              return (
                <li key={i} className="space-y-2 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="font-medium">{PAYMENT_METHODS[p.method].label}</span>
                      {adjust && p.adjustment !== 0 && (
                        <span className="ml-2 text-sm text-slate-500">
                          {adjust} ({p.adjustment > 0 ? '+' : '−'}
                          {money(Math.abs(p.adjustment))})
                        </span>
                      )}
                      {p.rounding !== 0 && <span className="ml-2 text-sm text-slate-500">redondeo {money(p.rounding)}</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-semibold tabular-nums">{money(p.amount)}</span>
                      <Button variant="ghost" size="sm" aria-label="Quitar pago" onClick={() => setPayments((list) => list.filter((_, j) => j !== i))}>
                        ✕
                      </Button>
                    </span>
                  </div>
                  {p.method === 'cash' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-sm text-slate-600">Paga con</span>
                        <div className="w-36">
                          <MoneyInput
                            data-tendered
                            value={p.tendered}
                            onChange={(c) => setTendered(i, c)}
                            presets={suggestions}
                            aria-label="Paga con"
                            invalid={p.tendered != null && p.tendered < p.amount && !short}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {suggestions.map((v) => (
                          <Button
                            key={v}
                            variant={p.tendered === v ? 'primary' : 'secondary'}
                            size="sm"
                            aria-pressed={p.tendered === v}
                            onClick={(e) => {
                              setTendered(i, v);
                              // En la PC el foco vuelve a «paga con»: Enter confirma y las flechas siguen desde ahí.
                              if (!touchScreen()) e.currentTarget.closest('li')?.querySelector<HTMLInputElement>('input[data-tendered]')?.focus();
                            }}
                          >
                            {v === p.amount ? 'Justo' : money(v)}
                          </Button>
                        ))}
                      </div>
                      {/* Debajo de los montos sugeridos, para que no se muevan mientras se escribe. */}
                      {short && (
                        <div className="space-y-2 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
                          <p className="text-sm font-medium text-amber-900">Faltan {money(short.restBase)}</p>
                          <div className="flex flex-wrap gap-2">
                            {accountAllowed && (
                              <Button size="sm" data-rest onClick={() => payRest(i, 'account')}>
                                Anotar el resto en la cuenta
                              </Button>
                            )}
                            <Button size="sm" variant="secondary" data-rest onClick={() => payRest(i, 'other')}>
                              El resto con otro medio
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {pending && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">{payments.length ? `Falta cubrir ${money(remaining)}` : '¿Cómo paga?'}</span>
              <button type="button" className="text-sm font-medium text-brand" onClick={() => setSplit((s) => !s)}>
                {split ? 'Pagar el resto' : 'Dividir el pago'}
              </button>
            </div>
            {split && (
              <div>
                <MoneyInput autoFocus value={partial} onChange={setPartial} placeholder="Monto con el medio que elijas" aria-label="Monto de este pago" />
                <p className="mt-1 text-xs text-slate-500">Escribí cuánto paga con un medio y tocá ese medio. Después elegí cómo paga el resto.</p>
              </div>
            )}
            <div
              ref={methodsRef}
              className="grid grid-cols-2 gap-2"
              onKeyDown={(e) => {
                const step = ARROW_STEPS[e.key];
                if (!step) return;
                const buttons = methodButtons();
                const current = buttons.indexOf(e.target as HTMLButtonElement);
                if (current < 0) return;
                e.preventDefault();
                const target = current + step;
                const lastRow = Math.floor((buttons.length - 1) / 2);
                if (target >= 0 && target < buttons.length) buttons[target]!.focus();
                // Bajando hacia la última fila incompleta: al último medio.
                else if (step === 2 && Math.floor(current / 2) < lastRow) buttons[buttons.length - 1]!.focus();
              }}
            >
              {methods.map((m) => {
                const preview = split && partial ? paymentForAmount(m, partial, remaining, settings) : paymentForBase(m, remaining, settings);
                const adjust = formatAdjust(settings.payments[m].adjust_bps);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => choose(m)}
                    className={cx(
                      'rounded-xl bg-white p-3 text-left ring-1 ring-slate-300 hover:ring-2 hover:ring-brand',
                      'focus-visible:bg-brand/10 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none',
                    )}
                  >
                    <span className="block font-medium">{PAYMENT_METHODS[m].label}</span>
                    <span className="block text-lg font-semibold tabular-nums">{money(preview.amount)}</span>
                    {adjust && <span className={cx('block text-xs', settings.payments[m].adjust_bps < 0 ? 'text-emerald-700' : 'text-amber-700')}>{adjust}</span>}
                  </button>
                );
              })}
            </div>
            {accountEnabled && !canUseAccount && (
              <p className="text-xs text-slate-500">Para anotar a cuenta corriente, pedile al dueño el permiso «Cuenta corriente».</p>
            )}
          </div>
        )}

        {customer && accountAllowed && (
          <p className="text-sm text-slate-600">
            Cliente: <strong>{customer.name}</strong> · {balanceLabel(customer.balance, money)}
            {customer.creditLimit != null && ` · límite ${money(customer.creditLimit)}`}
          </p>
        )}
        {overLimit && (
          <Alert tone={blockedByLimit ? 'error' : 'warning'}>
            Con esta compra la cuenta de {customer!.name} pasa su límite ({money(customer!.creditLimit ?? 0)}).
            {blockedByLimit ? ' Cobrá una parte con otro medio o pedile al dueño que suba el límite.' : ' Como sos el dueño, podés seguir igual.'}
          </Alert>
        )}
        {ready && change > 0 && (
          <div className="rounded-xl bg-emerald-50 p-3 text-center ring-1 ring-emerald-200">
            <p className="text-sm text-emerald-900">Vuelto</p>
            <p className="text-3xl font-bold text-emerald-900 tabular-nums">{money(change)}</p>
          </div>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        <p className="hidden text-xs text-slate-500 lg:block">
          <span className="font-medium">Teclado:</span>{' '}
          {pending
            ? 'flechas para elegir el medio de pago y Enter para seleccionarlo · Esc vuelve a la venta.'
            : anyShort
              ? 'si da menos de lo que hay que pagar, Enter pasa a las opciones para lo que falta (Tab cambia de opción) y Enter elige · Esc vuelve a la venta.'
              : lastMethod === 'cash'
                ? 'flechas para elegir con cuánto paga y ver el vuelto (o escribilo; vacío si paga justo) · Enter confirma la venta · Esc vuelve a la venta.'
                : 'Enter confirma la venta · Esc vuelve a la venta.'}
        </p>
        {/* Enter confirma la venta cuando ya está todo cubierto. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}
